import React, { useState, useCallback, useMemo } from 'react';
import { CheckCircle, AlertCircle, ChevronDown, ChevronUp, Plus, Edit2, X } from 'lucide-react';
import { FinancialStatement } from '../types';
import { getCategoryDisplayName } from '../utils/categoryNames';

interface SourceDataEditorProps {
    statements: FinancialStatement[];
    fieldCategories: Record<string, string[]>; // category -> fields
}

type YearCategoryFieldsType = Record<string, Record<string, { fields: string[], files: string[], missingFields: string[] }>>;

function processCategory(
    category: string,
    year: string,
    statement: Record<string, number>,
    fieldCategories: Record<string, string[]>,
    yearCategoryFields: YearCategoryFieldsType,
) {
    Object.keys(statement).forEach(field => {
        if (category) {
            if (statement[field] == null) {
                return;
            }
            if (!yearCategoryFields[year]) {
                yearCategoryFields[year] = {};
            }
            if (!yearCategoryFields[year][category]) {
                yearCategoryFields[year][category] = { fields: [], files: [], missingFields: [] };
            }

            if (yearCategoryFields[year][category].fields.indexOf(field) === -1) {
                yearCategoryFields[year][category].fields.push(field);
            }
            // yearCategoryFields[year][category].files.push(statement.file_id);
        }
    });
    const expectedFields = fieldCategories[category] || [];
    const missingFields = expectedFields.filter(field => !statement[field]);
    if (yearCategoryFields[year] && yearCategoryFields[year][category]) {
        yearCategoryFields[year][category].missingFields = missingFields;
    }
}

const SourceDataEditor: React.FC<SourceDataEditorProps> = ({
    statements,
    fieldCategories,
}) => {
    const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
    const [editingCategories, setEditingCategories] = useState<Set<string>>(new Set());
    // Store overrides: year -> field -> value
    const [overrides, setOverrides] = useState<Record<string, Record<string, number | null>>>({});
    // Track which fields have been manually added or removed
    const [fieldState, setFieldState] = useState<Record<string, Record<string, 'present' | 'removed' | 'added'>>>({});

    // Aggregate fields by year -> category across all uploaded files
    const getFieldsByYearAndCategory = useCallback((): Record<string, Record<string, { fields: string[], files: string[], missingFields: string[] }>> => {
        const yearCategoryFields: YearCategoryFieldsType = {};

        statements.forEach(statement => {
            const year = String(statement.year);
            if (statement.income_statement) {
                processCategory("income_statement", year, statement.income_statement, fieldCategories, yearCategoryFields);
            }
            if (statement.balance_sheet) {
                processCategory("balance_sheet", year, statement.balance_sheet, fieldCategories, yearCategoryFields);
            }
            if (statement.cash_flow_statement) {
                processCategory("cash_flow_statement", year, statement.cash_flow_statement, fieldCategories, yearCategoryFields);
            }
        });
        return yearCategoryFields;
    }, [statements, fieldCategories]);

    const fieldsByYearAndCategory = useMemo(() => getFieldsByYearAndCategory(), [getFieldsByYearAndCategory]);

    // Initialize expanded years on mount or when years change
    React.useEffect(() => {
        const years = Object.keys(fieldsByYearAndCategory);
        if (years.length > 0) {
            setExpandedYears(prev => {
                const newSet = new Set(prev);
                years.forEach(year => newSet.add(year));
                return newSet;
            });
        }
    }, [fieldsByYearAndCategory]);

    const toggleYear = (year: string) => {
        setExpandedYears(prev => {
            const newSet = new Set(prev);
            if (newSet.has(year)) {
                newSet.delete(year);
            } else {
                newSet.add(year);
            }
            return newSet;
        });
    };

    const toggleEditing = (year: string, category: string) => {
        const key = `${year}-${category}`;
        setEditingCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleAddField = (year: string, field: string) => {
        setFieldState(prev => ({
            ...prev,
            [year]: {
                ...prev[year],
                [field]: 'added'
            }
        }));
        setOverrides(prev => ({
            ...prev,
            [year]: {
                ...prev[year],
                [field]: null // Default value, user can edit
            }
        }));
    };

    const handleRemoveField = (year: string, field: string) => {
        setFieldState(prev => ({
            ...prev,
            [year]: {
                ...prev[year],
                [field]: 'removed'
            }
        }));
    };

    const handleRestoreField = (year: string, field: string) => {
        setFieldState(prev => {
            const newState = { ...prev };
            if (newState[year]) {
                delete newState[year][field];
            }
            return newState;
        });
    };

    const handleValueChange = (year: string, field: string, value: number | null) => {
        setOverrides(prev => ({
            ...prev,
            [year]: {
                ...prev[year],
                [field]: value
            }
        }));
    };

    const isFieldVisible = (year: string, field: string): boolean => {
        const state = fieldState[year]?.[field];
        return state !== 'removed';
    };

    const isFieldAdded = (year: string, field: string): boolean => {
        return fieldState[year]?.[field] === 'added';
    };

    if (Object.keys(fieldsByYearAndCategory).length === 0) {
        return null;
    }

    return (
        <div className="space-y-6 mb-6">
            <h3 className="font-semibold text-gray-900">Parsed Financial Data</h3>

            {Object.entries(fieldsByYearAndCategory)
                .sort(([yearA], [yearB]) => {
                    if (yearA === 'Unknown') return 1;
                    if (yearB === 'Unknown') return -1;
                    const numA = parseInt(yearA, 10);
                    const numB = parseInt(yearB, 10);
                    return numB - numA;
                })
                .map(([year, categories]) => {
                    const isExpanded = expandedYears.has(year);
                    return (
                        <div key={year} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                            <div
                                className="mb-4 cursor-pointer hover:bg-gray-100 rounded p-2 -m-2 transition-colors"
                                onClick={() => toggleYear(year)}
                            >
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {year === 'Unknown' ? 'Year Not Specified' : `Year ${year}`}
                                    </h3>
                                    {isExpanded ? (
                                        <ChevronUp className="w-5 h-5 text-gray-600" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-600" />
                                    )}
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="space-y-4">
                                    {Object.entries(categories).map(([category, data]) => {
                                        const categoryKey = `${year}-${category}`;
                                        const isEditing = editingCategories.has(categoryKey);
                                        const allFields = [...data.fields, ...data.missingFields];

                                        return (
                                            <div key={category} className="border border-gray-200 rounded-lg p-4 bg-white">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                                        <h4 className="font-medium text-gray-900">
                                                            {getCategoryDisplayName(category)}
                                                        </h4>
                                                        <span className="text-sm text-gray-500">
                                                            ({data.fields.length} of {allFields.length} fields)
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleEditing(year, category);
                                                            }}
                                                            className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                                                            type="button"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>


                                                <div className="space-y-3">
                                                    {/* Parsed Fields */}
                                                    {data.fields.filter(field => isFieldVisible(year, field)).length > 0 && (
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <CheckCircle className="w-4 h-4 text-green-600" />
                                                                <span className="text-sm font-medium text-green-700">
                                                                    Parsed Fields ({data.fields.filter(f => isFieldVisible(year, f)).length})
                                                                </span>
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                                                {data.fields.filter(field => isFieldVisible(year, field)).map(field => {
                                                                    const overrideValue = overrides[year]?.[field];
                                                                    return (
                                                                        <div key={field} className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1">
                                                                            <span className="text-xs text-green-800 font-medium flex-1">
                                                                                {field.replace(/_/g, ' ')}
                                                                            </span>
                                                                            {isEditing && (
                                                                                <>
                                                                                    <input
                                                                                        type="number"
                                                                                        value={overrideValue ?? ''}
                                                                                        onChange={(e) => handleValueChange(year, field, e.target.value ? parseFloat(e.target.value) : null)}
                                                                                        className="w-20 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                                                        placeholder="Value"
                                                                                    />
                                                                                    <button
                                                                                        onClick={() => handleRemoveField(year, field)}
                                                                                        className="p-0.5 hover:bg-red-100 rounded"
                                                                                        type="button"
                                                                                    >
                                                                                        <X className="w-3 h-3 text-red-600" />
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Added Fields */}
                                                    {data.missingFields.filter(field => isFieldAdded(year, field)).length > 0 && (
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <CheckCircle className="w-4 h-4 text-blue-600" />
                                                                <span className="text-sm font-medium text-blue-700">
                                                                    Manually Added Fields ({data.missingFields.filter(f => isFieldAdded(year, f)).length})
                                                                </span>
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                                                {data.missingFields.filter(field => isFieldAdded(year, field)).map(field => {
                                                                    const overrideValue = overrides[year]?.[field];
                                                                    return (
                                                                        <div key={field} className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                                                                            <span className="text-xs text-blue-800 font-medium flex-1">
                                                                                {field.replace(/_/g, ' ')}
                                                                            </span>
                                                                            {isEditing && (
                                                                                <>
                                                                                    <input
                                                                                        type="number"
                                                                                        value={overrideValue ?? ''}
                                                                                        onChange={(e) => handleValueChange(year, field, e.target.value ? parseFloat(e.target.value) : null)}
                                                                                        className="w-20 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                                                        placeholder="Value"
                                                                                    />
                                                                                    <button
                                                                                        onClick={() => handleRestoreField(year, field)}
                                                                                        className="p-0.5 hover:bg-red-100 rounded"
                                                                                        type="button"
                                                                                    >
                                                                                        <X className="w-3 h-3 text-red-600" />
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Missing Fields */}
                                                    {data.missingFields.filter(field => !isFieldAdded(year, field)).length > 0 && (
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <AlertCircle className="w-4 h-4 text-gray-500" />
                                                                <span className="text-sm font-medium text-gray-600">
                                                                    Missing Fields ({data.missingFields.filter(f => !isFieldAdded(year, f)).length})
                                                                </span>
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                                                {data.missingFields.filter(field => !isFieldAdded(year, field)).map(field => (
                                                                    <div key={field} className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                                                                        <span className="text-xs text-gray-500 font-medium flex-1">
                                                                            {field.replace(/_/g, ' ')}
                                                                        </span>
                                                                        {isEditing && (
                                                                            <button
                                                                                onClick={() => handleAddField(year, field)}
                                                                                className="p-0.5 hover:bg-green-100 rounded"
                                                                                type="button"
                                                                            >
                                                                                <Plus className="w-3 h-3 text-green-600" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Source Files */}
                                                <div className="mt-2 text-xs text-gray-500">
                                                    Source files: {data.files.join(', ')}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
        </div>
    );
};

export default SourceDataEditor;

